import { AssetError } from './errors.ts';
import type { AssetRecordV1 } from './types.ts';

export interface PdfPreviewOptions {
  readonly pageIndex?: number;
  readonly maxWidthPx?: number;
  readonly outputScale?: number;
  readonly workerSrc?: string;
  readonly background?: string;
  readonly transparent?: boolean;
}

export interface PdfPreviewResult {
  readonly canvas: HTMLCanvasElement;
  readonly pageIndex: number;
  readonly widthCssPx: number;
  readonly heightCssPx: number;
  dispose(): void;
}

export async function renderPdfPagePreview(
  record: AssetRecordV1,
  originalBytes: Uint8Array,
  options: PdfPreviewOptions = {},
): Promise<PdfPreviewResult> {
  if (record.metadata.kind !== 'pdf') {
    throw new AssetError('ASSET_FORMAT_UNSUPPORTED', 'El preview PDF requiere un activo PDF.');
  }
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    throw new AssetError(
      'ASSET_PDF_PREVIEW_REQUIRES_DOM',
      'El preview usa PDF.js y Canvas, por lo que requiere un navegador con DOM.',
    );
  }
  const pageIndex = options.pageIndex ?? record.metadata.selectedPageIndex;
  if (!Number.isInteger(pageIndex) || pageIndex < 0 || pageIndex >= record.metadata.pageCount) {
    throw new AssetError(
      'ASSET_PDF_PAGE_OUT_OF_RANGE',
      'La pagina solicitada para preview no existe.',
      { pageIndex, pageCount: record.metadata.pageCount },
    );
  }

  try {
    const pdfjs = await import('pdfjs-dist');
    if (options.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = options.workerSrc;
    const loadingTask = pdfjs.getDocument({ data: Uint8Array.from(originalBytes) });
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(pageIndex + 1);
    const baseViewport = page.getViewport({ scale: 1 });
    const maxWidth = Math.max(64, options.maxWidthPx ?? 720);
    const cssScale = Math.min(1, maxWidth / baseViewport.width);
    const viewport = page.getViewport({ scale: cssScale });
    const outputScale = Math.max(1, options.outputScale ?? globalThis.devicePixelRatio ?? 1);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { alpha: options.transparent === true });
    if (!context) throw new Error('CanvasRenderingContext2D no disponible');
    canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
    canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
    canvas.style.width = Math.floor(viewport.width) + 'px';
    canvas.style.height = Math.floor(viewport.height) + 'px';
    await page.render({
      canvasContext: context,
      viewport,
      transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
      background: options.transparent === true
        ? 'rgba(0, 0, 0, 0)'
        : options.background ?? '#ffffff',
    }).promise;
    return {
      canvas,
      pageIndex,
      widthCssPx: viewport.width,
      heightCssPx: viewport.height,
      dispose() {
        canvas.width = 1;
        canvas.height = 1;
        void page.cleanup();
        void pdf.destroy();
        void loadingTask.destroy();
      },
    };
  } catch (cause) {
    if (cause instanceof AssetError) throw cause;
    throw new AssetError(
      'ASSET_PDF_PREVIEW_FAILED',
      'PDF.js no pudo generar el preview de la pagina seleccionada.',
      { pageIndex },
      { cause },
    );
  }
}
